// src/views/ChatDock.jsx
// ASK THE LIBRARY FROM ANY SCREEN — a small chat button pinned to the
// bottom-right corner of every staff screen. Click it and a chat box opens
// over whatever is on screen; minimize it (—, or Esc) and it's a button again.
//
// It is a second window onto the SAME conversation as the Lender Library
// page's Ask tab: the thread, a running answer, the draft and the lender chips
// live in the per-person store in src/libraryChat.js, and the box renders
// them with the shared components in src/views/ChatThread.jsx (compact). So a
// question asked here is on the Library page and vice versa, an answer keeps
// streaming while the box is minimized, hidden or unmounted, and both show
// it live. An answer that lands while no chat is on screen puts a dot on the
// button (the store's `fresh`). App mounts the box once, outside the view
// switch, and hides it on the Library page itself (the same thread is on
// screen there — unless the New Call console is open over it).
//
// Sources: a click opens the ORIGINAL file at that page in a new tab (signed
// link) rather than switching to the Library page's viewer — the bubble
// floats over a live call or file, and that context must stay where it is.
// "Library ↗" in the header is there for the full page viewer (disabled while
// a call is open — see libraryLocked).
//
// Model output is rendered as TEXT only — never as HTML (ChatThread).
//
// The Whiteboard bubble (src/views/WhiteboardDock.jsx) sits in the opposite
// corner. Opening either panel announces it ("lf-dock-open", src/ui.jsx);
// where the two panels can't sit side by side (docksCollide) the other one
// minimizes — also when the window narrows with both open (useDockRoom) —
// and an open panel sits one layer above the other bubble. Over the New Call
// console, the console's action bar keeps this corner clear (`dockRight`).

import React, { useState, useEffect, useRef, useCallback } from "react";
import { C, Ic } from '../theme';
import { announceDockOpen, useDockRoom } from '../ui';
import { getSignedUrl } from '../kb';
import { getChatStore, useLibraryChat } from '../libraryChat';
import { ChatThread, ChatComposer } from './ChatThread';

const FONT = "'Mulish',system-ui,sans-serif";
// Above page content, the drawers and the New Call console (400) so it can be
// used mid-call; below the console's disposition modal (420), the inbound-call
// toast (500) and every screen's modals (1000+).
const Z = 405;
const EXAMPLES = [
  "What's the minimum age for HomeSafe in Florida?",
  "How is a non-borrowing spouse handled on a HECM?",
  "Does SmartFi allow a condo that isn't FHA-approved?",
];
const errMsg = function (e, fb) { return (e && e.message) || fb || "unknown error"; };
const chatIcon = <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" /></svg>;
const headBtn = { height: 30, minWidth: 30, padding: "0 9px", borderRadius: 8, border: "1px solid rgba(242,239,231,0.22)", background: "transparent", color: C.cream, cursor: "pointer", fontFamily: FONT, fontSize: 11.5, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, flexShrink: 0 };

// staff: whose conversation (one per person). hidden: show nothing (the
// Library page, the nav drawer) — the conversation and a running answer live
// in the store, so nothing is lost. onOpenLibrary: go to the Lender Library page.
// libraryLocked: a call is open — the console covers the screen, so going to
// the Library would only change (and drop the file in) the page underneath;
// "Library ↗" and the no-library link are disabled and say why.
// practice: Practice mode is on (Max only) — the conversation is kept in
// memory only (src/libraryChat.js) and the header says so.
const LOCKED_TITLE = "Close the call to open the Lender Library";
function ChatDock({ staff, hidden, onOpenLibrary, libraryLocked, practice }) {
  const who = staff || "reece";
  const store = getChatStore(who);
  const [open, setOpen] = useState(false);
  const [srcErr, setSrcErr] = useState(null);   // {key:"turnId:n", text}
  const busy = useLibraryChat(who, function (s) { return s.busy; });
  const fresh = useLibraryChat(who, function (s) { return s.fresh; });
  const turnCount = useLibraryChat(who, function (s) { return s.turns.length; });
  const unread = !!fresh && !fresh.seen;
  const mountedRef = useRef(true);
  const rootRef = useRef(null);
  const taRef = useRef(null);
  const scrollRef = useRef(null);
  const launcherRef = useRef(null);
  const stickRef = useRef(true);               // follow the bottom of the thread unless he scrolled up
  const focusRef = useRef(false);              // put the cursor in the box when he opens it (not when it reappears on its own)
  const openRef = useRef(open); openRef.current = open;
  const shown = open && !hidden;
  const libRef = useRef(onOpenLibrary); libRef.current = onOpenLibrary;
  const lockedRef = useRef(!!libraryLocked); lockedRef.current = !!libraryLocked;

  useEffect(function () { mountedRef.current = true; return function () { mountedRef.current = false; }; }, []);
  // While the box is on screen an answer that lands was watched — no dot.
  useEffect(function () {
    store.setViewing("dock", shown);
    return function () { store.setViewing("dock", false); };
  }, [store, shown]);
  // On screen again: clear the dot and jump to the newest message. The cursor
  // only goes into the box when he opened it — not when the box reappears
  // by itself (leaving the Library screen, closing the menu).
  useEffect(function () {
    if (!shown) return;
    stickRef.current = true;
    const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight;
    if (!focusRef.current) return;
    focusRef.current = false;
    const ta = taRef.current; if (ta) { try { ta.focus({ preventScroll: true }); } catch (e) { ta.focus(); } }
  }, [shown]);
  useEffect(function () { if (shown && unread) store.markSeen("dock"); }, [shown, unread, store]);
  // Follow the thread as it grows — only if he's already at the bottom.
  const follow = useCallback(function () {
    if (!stickRef.current) return;
    const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const close = useCallback(function (refocus) {
    setOpen(false);
    if (refocus) setTimeout(function () { if (launcherRef.current) launcherRef.current.focus(); }, 0);
  }, []);
  // Esc inside the box minimizes it — and ONLY it: this capture listener is
  // registered once, before the New Call console's own (re-registered on
  // every render), so a keypress meant for the chat can't also close a call.
  useEffect(function () {
    const h = function (e) {
      if (e.key !== "Escape" || !openRef.current) return;
      const root = rootRef.current;
      if (!root || !e.target || !root.contains(e.target)) return;
      e.stopImmediatePropagation(); e.preventDefault();
      close(true);
    };
    window.addEventListener("keydown", h, true);
    return function () { window.removeEventListener("keydown", h, true); };
  }, [close]);
  // The Whiteboard panel opened where the two can't both fit, or the window
  // narrowed with both open: make room (src/ui.jsx).
  const minimize = useCallback(function () { setOpen(false); }, []);
  useDockRoom("chat", open, minimize);
  const openIt = function () { focusRef.current = true; setOpen(true); announceDockOpen("chat"); };

  // A source opens the original file at that page in a new tab. The tab is
  // opened inside the click (popup blockers allow that) and pointed at the
  // signed link once it arrives; pasted notes have no file, so say so instead.
  const openSource = useCallback(function (t, s) {
    const key = t.id + ":" + s.n;
    let w = null;
    try { w = window.open("", "_blank"); } catch (e) { w = null; }
    if (w) { try { w.opener = null; } catch (e) { /* ignore */ } }
    setSrcErr(null);
    Promise.resolve().then(function () { return getSignedUrl({ docId: s.docId }, 3600); }).then(function (url) {
      if (!url) throw new Error("No link came back for this file.");
      const href = url + "#page=" + s.page;
      if (w && !w.closed) w.location.replace(href);
      else window.open(href, "_blank", "noopener,noreferrer");
    }).catch(function (e) {
      if (w && !w.closed) { try { w.close(); } catch (x) { /* ignore */ } }
      if (mountedRef.current) setSrcErr({ key: key, text: errMsg(e, "Couldn't open that document.") });
    });
  }, []);
  const goLibrary = useCallback(function () {
    if (lockedRef.current) return;
    setOpen(false);
    if (typeof libRef.current === "function") libRef.current();
  }, []);
  const newChat = function () {
    if (turnCount && !window.confirm("Start a new chat? This clears the conversation — here and on the Lender Library page — on this device.")) return;
    store.newChat(); setSrcErr(null);
  };
  const pickExample = function (q) { store.setDraft(q); setTimeout(function () { if (taRef.current) taRef.current.focus(); }, 0); };
  const onScroll = function () {
    const el = scrollRef.current; if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };
  const onSent = useCallback(function () { stickRef.current = true; setSrcErr(null); }, []);

  const css = `@keyframes lf-dock-spin{to{transform:rotate(360deg)}}@keyframes lf-dock-in{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
.lf-dock{position:fixed;right:20px;bottom:20px;z-index:${Z};font-family:${FONT};}
.lf-dock.is-open{z-index:${Z + 1};}
.lf-dock-panel{width:400px;height:min(640px,calc(100vh - 100px));display:flex;flex-direction:column;background:${C.bg};border:1px solid ${C.border};border-radius:16px;box-shadow:0 18px 50px rgba(6,26,48,0.28);overflow:hidden;animation:lf-dock-in .16s ease-out;}
.lf-dock-launch{position:relative;width:56px;height:56px;border-radius:50%;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;color:${C.cream};background:linear-gradient(135deg,${C.navy2},${C.navyDeep});box-shadow:0 6px 22px rgba(6,26,48,0.35);transition:transform .15s ease,box-shadow .15s ease;}
.lf-dock-launch:hover{transform:translateY(-2px);box-shadow:0 10px 28px rgba(6,26,48,0.42);}
@media (max-width:560px){.lf-dock{right:12px;bottom:12px;}.lf-dock.is-open{left:12px;}.lf-dock-panel{width:auto;height:calc(100vh - 88px);}}
@media (prefers-reduced-motion:reduce){.lf-dock-panel{animation:none;}.lf-dock-launch{transition:none;}}`;

  if (hidden) return null;
  if (!open) return (<div ref={rootRef} className="lf-dock" data-lf-dock="">
    <style>{css}</style>
    <button ref={launcherRef} className="lf-dock-launch" onClick={openIt}
      title={busy ? "Ask the Library — an answer is being written" : unread ? "Ask the Library — a new answer is waiting" : "Ask the Library — your lender guidelines chatbot"}
      aria-label={"Open the Ask the Library chat" + (busy ? " (answer in progress)" : unread ? " (new answer)" : "")}>
      {chatIcon}
      {busy && <span aria-hidden="true" style={{ position: "absolute", inset: -4, borderRadius: "50%", border: "3px solid " + C.clay, borderTopColor: "transparent", animation: "lf-dock-spin .9s linear infinite" }} />}
      {unread && !busy && <span aria-hidden="true" style={{ position: "absolute", top: 2, right: 2, width: 14, height: 14, borderRadius: "50%", background: C.clay, border: "2px solid " + C.bg }} />}
    </button>
  </div>);

  return (<div ref={rootRef} className="lf-dock is-open" data-lf-dock="">
    <style>{css}</style>
    <section className="lf-dock-panel" role="dialog" aria-modal="false" aria-label="Ask the Library chat">
      {/* ── header ── */}
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 8, padding: "10px 10px 10px 14px", background: C.navyDeep, color: C.cream }}>
        <span style={{ display: "flex", color: C.clay }}>{Ic.book}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, letterSpacing: .2 }}>Ask the Library</div>
          {practice
            ? <div data-practice-note="" style={{ fontSize: 10.5, color: C.claySoft, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title="Practice mode is on — this chat is kept on this screen only and is not saved">Practice mode — this chat isn't saved</div>
            : <div style={{ fontSize: 10.5, color: "rgba(242,239,231,0.65)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>From your lender documents</div>}
        </div>
        <button onClick={newChat} disabled={!turnCount && !busy} title="Start a new chat" style={{ ...headBtn, opacity: !turnCount && !busy ? .45 : 1, cursor: !turnCount && !busy ? "default" : "pointer" }}>New chat</button>
        <button onClick={goLibrary} disabled={!!libraryLocked}
          title={libraryLocked ? LOCKED_TITLE : "Open the Lender Library page — the same conversation, plus lender filters and the page viewer"}
          aria-label={libraryLocked ? LOCKED_TITLE : "Open the Lender Library screen"}
          style={{ ...headBtn, opacity: libraryLocked ? .45 : 1, cursor: libraryLocked ? "not-allowed" : "pointer" }}>Library ↗</button>
        <button onClick={function () { close(true); }} title="Minimize (Esc)" aria-label="Minimize the chat" style={{ ...headBtn, fontSize: 16, padding: 0, width: 30 }}>–</button>
      </div>

      {/* ── thread (shared with the Library page) ── */}
      <div ref={scrollRef} onScroll={onScroll} style={{ flex: 1, overflowY: "auto", padding: "12px 12px 6px" }}>
        {turnCount === 0 && !busy && <div style={{ background: C.card, border: "1px solid " + C.border, borderRadius: "12px 12px 12px 4px", padding: "12px 14px", boxSizing: "border-box", marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>Ask like you'd ask your AE</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4, lineHeight: 1.5 }}>Guidelines, ages, limits, overlays, pricing — answered from your uploaded lender documents. Tap Sources under an answer, or ask “where does it say that?”, to see the pages.</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
            {EXAMPLES.map(function (q) { return <button key={q} onClick={function () { pickExample(q); }} style={{ textAlign: "left", fontSize: 12, fontWeight: 700, color: C.accentLight, background: C.accentGlow, border: "1px solid " + C.border, borderRadius: 10, padding: "7px 10px", cursor: "pointer", fontFamily: FONT, whiteSpace: "normal" }}>{q}</button>; })}
          </div>
          <div style={{ fontSize: 10.5, color: C.textDim, marginTop: 10, lineHeight: 1.45 }}>Same conversation as the Lender Library page — that page adds lender filters and the page viewer.</div>
        </div>}
        <ChatThread staff={who} compact onOpenSrc={openSource} goLibrary={libraryLocked ? null : goLibrary} srcErr={srcErr} liveShown={shown} onUpdate={follow} idPrefix="kb-dock-turn-" />
      </div>

      {/* ── composer (shared; carries the deal-basics chip) ── */}
      <div style={{ flexShrink: 0, padding: "8px 10px 10px", borderTop: "1px solid " + C.border, background: C.bg }}>
        <ChatComposer staff={who} compact inputRef={taRef} onSent={onSent} ariaLabel="Ask the library (chat box)" placeholder="Ask about guidelines, ages, limits…" />
      </div>
    </section>
  </div>);
}

// Memoized: App re-renders constantly (save status, polls); the dock's props
// are a string, three booleans and a stable function.
const ChatDockMemo = React.memo(ChatDock);
export { ChatDockMemo as ChatDock };
